import { useState } from "react";
import type { PortalAction, PortalEvent } from "@/data/portal/intelligence";
import { createPortalEvent } from "@/data/portal/intelligence";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Calendar, CheckCircle, DollarSign, FileText, Camera,
  AlertCircle, Send, X, Upload,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  jobId: string;
  actions: PortalAction[];
  activeActionId: string | null;
  onActionComplete: (event: PortalEvent) => void;
  onClose: () => void;
}

export function PortalActionLayer({ jobId, actions, activeActionId, onActionComplete, onClose }: Props) {
  const action = actions.find(a => a.id === activeActionId);
  if (!action) return null;

  return (
    <div className="rounded-2xl border border-primary/20 bg-card overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-border/30">
        <div className="flex items-center gap-2">
          <ActionIcon type={action.type} />
          <h3 className="text-sm font-semibold text-foreground">{action.label}</h3>
        </div>
        <button onClick={onClose} className="h-7 w-7 rounded-lg flex items-center justify-center hover:bg-muted transition-colors">
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
      <div className="p-4">
        {action.type === "confirm_appointment" && (
          <ConfirmAppointmentAction action={action} jobId={jobId} onComplete={onActionComplete} />
        )}
        {action.type === "approve_change_order" && (
          <ApproveChangeOrderAction action={action} jobId={jobId} onComplete={onActionComplete} />
        )}
        {action.type === "pay_invoice" && (
          <PayInvoiceAction action={action} jobId={jobId} onComplete={onActionComplete} />
        )}
        {action.type === "submit_issue" && (
          <SubmitIssueAction jobId={jobId} onComplete={onActionComplete} />
        )}
        {action.type === "upload_photos" && (
          <UploadPhotosAction jobId={jobId} onComplete={onActionComplete} />
        )}
      </div>
    </div>
  );
}

function ActionIcon({ type }: { type: PortalAction["type"] }) {
  const config = {
    confirm_appointment: { icon: Calendar, class: "bg-primary/10 text-primary" },
    approve_change_order: { icon: FileText, class: "bg-warning/10 text-warning" },
    pay_invoice: { icon: DollarSign, class: "bg-success/10 text-success" },
    submit_issue: { icon: AlertCircle, class: "bg-destructive/10 text-destructive" },
    upload_photos: { icon: Camera, class: "bg-accent/80 text-accent-foreground" },
  };
  const c = config[type];
  const Icon = c.icon;
  return (
    <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center", c.class)}>
      <Icon className="h-3.5 w-3.5" />
    </div>
  );
}

function ConfirmAppointmentAction({ action, jobId, onComplete }: { action: PortalAction; jobId: string; onComplete: (e: PortalEvent) => void }) {
  const [confirmed, setConfirmed] = useState(false);

  if (confirmed) return <SuccessMessage message="Appointment confirmed. We'll see you then!" />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{action.description}</p>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => {
          setConfirmed(true);
          onComplete(createPortalEvent(jobId, "appointment_confirmed", "Customer confirmed appointment", "customer", action.data));
          toast.success("Appointment confirmed");
        }}>
          <CheckCircle className="h-4 w-4 mr-1.5" /> Confirm
        </Button>
        <Button size="sm" variant="outline" onClick={() => {
          onComplete(createPortalEvent(jobId, "reschedule_requested", "Customer requested reschedule", "customer"));
          toast.info("Reschedule request submitted");
        }}>
          Request Reschedule
        </Button>
      </div>
    </div>
  );
}

function ApproveChangeOrderAction({ action, jobId, onComplete }: { action: PortalAction; jobId: string; onComplete: (e: PortalEvent) => void }) {
  const [done, setDone] = useState(false);
  if (done) return <SuccessMessage message="Change order response submitted." />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{action.description}</p>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => {
          setDone(true);
          onComplete(createPortalEvent(jobId, "change_order_approved", "Customer approved change order", "customer", action.data));
          toast.success("Change order approved");
        }}>
          <CheckCircle className="h-4 w-4 mr-1.5" /> Approve
        </Button>
        <Button size="sm" variant="outline" onClick={() => {
          setDone(true);
          onComplete(createPortalEvent(jobId, "change_order_rejected", "Customer declined change order", "customer", action.data));
          toast.info("Change order declined");
        }}>
          Decline
        </Button>
      </div>
    </div>
  );
}

function PayInvoiceAction({ action, jobId, onComplete }: { action: PortalAction; jobId: string; onComplete: (e: PortalEvent) => void }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{action.description}</p>
      <Button size="sm" onClick={() => {
        onComplete(createPortalEvent(jobId, "payment_initiated", "Customer initiated payment", "customer", action.data));
        toast.info("Payment feature coming soon");
      }}>
        <DollarSign className="h-4 w-4 mr-1.5" /> Pay Now
      </Button>
    </div>
  );
}

function SubmitIssueAction({ jobId, onComplete }: { jobId: string; onComplete: (e: PortalEvent) => void }) {
  const [category, setCategory] = useState("quality");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);

  if (submitted) return <SuccessMessage message="Issue submitted. We'll be in touch shortly." />;

  return (
    <div className="space-y-3">
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="quality">Quality Concern</SelectItem>
          <SelectItem value="damage">Damage Report</SelectItem>
          <SelectItem value="schedule">Scheduling Issue</SelectItem>
          <SelectItem value="communication">Communication Issue</SelectItem>
          <SelectItem value="other">Other</SelectItem>
        </SelectContent>
      </Select>
      <Textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Please describe the issue..."
        className="min-h-[80px] text-sm"
      />
      <Button size="sm" disabled={!description.trim()} onClick={() => {
        setSubmitted(true);
        onComplete(createPortalEvent(jobId, "issue_submitted", description, "customer", { category }));
        toast.success("Issue submitted");
      }}>
        <Send className="h-4 w-4 mr-1.5" /> Submit Issue
      </Button>
    </div>
  );
}

function UploadPhotosAction({ jobId, onComplete }: { jobId: string; onComplete: (e: PortalEvent) => void }) {
  const [uploaded, setUploaded] = useState(false);
  if (uploaded) return <SuccessMessage message="Photos received. Thank you!" />;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Share photos of your project or any concerns.</p>
      <div className="border-2 border-dashed border-border/60 rounded-xl p-6 text-center hover:border-primary/30 transition-colors cursor-pointer"
        onClick={() => {
          setUploaded(true);
          onComplete(createPortalEvent(jobId, "photos_uploaded", "Customer uploaded photos", "customer"));
          toast.success("Photos uploaded");
        }}
      >
        <Upload className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm text-muted-foreground">Tap to select photos</p>
        <p className="text-xs text-muted-foreground/60 mt-1">JPG, PNG up to 10MB each</p>
      </div>
    </div>
  );
}

function SuccessMessage({ message }: { message: string }) {
  return (
    <div className="text-center py-4">
      <CheckCircle className="h-8 w-8 mx-auto text-success mb-2" />
      <p className="text-sm font-medium text-foreground">{message}</p>
    </div>
  );
}
